import { DeliveryPickerWrapper, StyledBackButton, StyledDeliveryPickerHeading } from './DeliverySelector.styles';
import DeliveryBox from '@molecules/DeliveryBox/DeliveryBox';
import Icon from '@atoms/Icon/Icon';
import Store from '@icons/store.svg';
import Text from '@atoms/Text/Text';
import HomeDelivery from '@icons/home-delivery.svg';
import React, { Dispatch, Key, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Config from '@config';
import { trackDeliveryPickerToggle } from '@helpers/analyticsHelpers/trackDeliveryPicker';
import useTranslation from 'next-translate/useTranslation';
import SlotSelector from '@organisms/SlotSelector/SlotSelector';
import ArrowLeft from '@icons/arrow-left-small.svg';
import PickUpInStoreSelector from '@molecules/PickUpInStoreSelector/PickUpInStoreSelector';
import HomeDeliveryAddressSelector from '@molecules/HomeDeliveryAddressSelector/HomeDeliveryAddressSelector';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import {
  selectCheckout,
  selectPossiblyWrongStoreConfirmed,
  setErrorMessage,
  setPossiblyWrongStoreConfirmed,
} from '@slices/checkoutSlice';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useCustomer from '@hooks/useCustomer';

export type DeliveryPickerNavigationStateType = 'chooseDeliveryMode' | 'pickUpInStore' | 'homeDelivery' | 'chooseSlot';

interface Props {
  setShowPickedSlotNotification?: Dispatch<SetStateAction<boolean>>;
}

const DeliverySelector = ({ setShowPickedSlotNotification }: Props) => {
  const { t } = useTranslation('deliverypicker');
  const { store } = useStore();
  const { cart } = useCart();
  const autoFocusRef = useRef<HTMLDivElement>(null);
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(
    cart?.deliveryModeCode ? cart.deliveryModeCode : 'chooseDeliveryMode'
  );
  const dispatch = useAppDispatch();
  const possiblyWrongStoreConfirmed = useAppSelector(selectPossiblyWrongStoreConfirmed);
  const checkout = useAppSelector(selectCheckout);
  const { customer } = useCustomer();

  const isOpenOrderAndFranchise = useMemo(
    () => Boolean(cart?.orderReference) && cart?.franchiseStore,
    [cart?.franchiseStore, cart?.orderReference]
  );

  const [navigationState, setNavigationState] = useState(selectedDeliveryMode);

  const initShowSlotSelector = () => {
    const showSlots = store?.storeId && store?.activelySelected && !!cart?.deliveryModeCode;
    const isKPLStore = store?.external;

    switch (selectedDeliveryMode) {
      case Config.HOME_DELIVERY:
        return showSlots && cart?.deliveryAddress?.postalCode;
      case Config.PICKUP_IN_STORE:
        return showSlots && !isKPLStore;
      default:
        return false;
    }
  };
  const [showSlotSelector] = useState(initShowSlotSelector());

  const onTabChange = (tab: Key) => {
    const modeCode = tab === Config.HOME_DELIVERY ? Config.HOME_DELIVERY : Config.PICKUP_IN_STORE;
    setNavigationState(modeCode as DeliveryPickerNavigationStateType);
    setSelectedDeliveryMode(modeCode);

    trackDeliveryPickerToggle(
      modeCode === Config.HOME_DELIVERY ? 'toggled_to_home_delivery' : 'toggled_to_click_and_collect',
      'delivery_widget'
    );
  };

  useEffect(() => {
    if (navigationState === 'chooseDeliveryMode' && autoFocusRef.current) {
      setTimeout(() => autoFocusRef.current?.focus(), 0);
    }
  }, [navigationState]);

  useEffect(() => {
    if (showSlotSelector) {
      setNavigationState('chooseSlot');
    }
  }, [showSlotSelector]);

  useEffect(() => {
    if (isOpenOrderAndFranchise) {
      setNavigationState('chooseSlot');
    }
  }, [isOpenOrderAndFranchise]);

  const BackButton = (state: string) =>
    !isOpenOrderAndFranchise && (
      <StyledBackButton theme="transparent" onClick={() => setNavigationState(state)} data-testid="navigate-back-button">
        <Icon svg={ArrowLeft} size={16} />
      </StyledBackButton>
    );

  return (
    <DeliveryPickerWrapper data-testid="delivery-picker-widget">
      <StyledDeliveryPickerHeading variant="h3">
        {t(
          `deliveryPicker->titles->${navigationState}${
            navigationState === Config.HOME_DELIVERY && customer?.isB2BCustomer ? '_b2b' : ''
          }`
        )}
      </StyledDeliveryPickerHeading>

      {navigationState === 'chooseDeliveryMode' && (
        <>
          <DeliveryBox
            onClick={() => onTabChange(Config.PICKUP_IN_STORE)}
            icon={<Icon size={24} svg={Store} />}
            data-testid="delivery-method-pickUpInStore"
            ref={autoFocusRef}
          >
            <Text type="subtitle" size="medium">
              {t('deliveryPicker->box->pickUpInStore')}
            </Text>
            <Text type="body" size="medium">
              {t('deliveryPicker->price->pickUpInStore')}
            </Text>
          </DeliveryBox>

          <DeliveryBox
            onClick={() => onTabChange(Config.HOME_DELIVERY)}
            icon={<Icon size={24} svg={HomeDelivery} />}
            data-testid="delivery-method-homeDelivery"
          >
            <Text type="subtitle" size="medium">
              {t(`deliveryPicker->box->homeDelivery${customer?.isB2BCustomer ? '_b2b' : ''}`)}
            </Text>
            <Text type="body" size="medium">
              {t('deliveryPicker->price->homeDelivery')}
            </Text>
          </DeliveryBox>
        </>
      )}

      {navigationState === Config.PICKUP_IN_STORE && (
        <>
          {BackButton('chooseDeliveryMode')}
          <PickUpInStoreSelector
            title={t(`deliveryPicker->info->${Config.PICKUP_IN_STORE}`)}
            afterSubmit={() => {
              setNavigationState('chooseSlot');
              if (possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.UNCONFIRMED) {
                dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY));
              }
            }}
          />
        </>
      )}

      {navigationState === Config.HOME_DELIVERY && (
        <>
          {BackButton('chooseDeliveryMode')}
          <HomeDeliveryAddressSelector afterSubmit={() => setNavigationState('chooseSlot')} />
        </>
      )}

      {navigationState === 'chooseSlot' && (
        <>
          {BackButton(selectedDeliveryMode)}
          {checkout.delivery.errorMessage && (
            <Text type="body" size="medium" color="error">
              {t(`deliveryPicker->${checkout.delivery.errorMessage}`)}
            </Text>
          )}
          <SlotSelector
            deliveryMode={selectedDeliveryMode as 'homeDelivery' | 'pickUpInStore'}
            changeDeliveryHandler={() => setNavigationState('chooseDeliveryMode')}
            onSubmitted={() => {
              if (
                cart?.deliveryModeCode === Config.PICKUP_IN_STORE &&
                possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.UNCONFIRMED
              ) {
                dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY));
              }
              dispatch(setErrorMessage(''));

              if (setShowPickedSlotNotification) setShowPickedSlotNotification(true);
            }}
          />
        </>
      )}
    </DeliveryPickerWrapper>
  );
};
export default DeliverySelector;
